import {
  Box,
  Button,
  Card,
  Divider,
  Flex,
  SimpleGrid,
  Text,
} from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { AdminBadge } from '../../../components/StatusBadge/StatusBadge'
import {
  CustomerDataApiRuntimeCustomer,
  CustomerDataApiRuntimeSubgraph,
  CustomerDataApiServiceStatus,
  CustomerDataApiStatusDeploymentRef,
} from '../../../services/customerDataApiService'
import { formatAdminDateTime } from '../../../utils/dates'

const statusColor = (status?: string | null, accessible?: boolean) => {
  if (accessible === false) return 'red'
  switch (status) {
    case 'READY':
    case 'SUCCESS':
      return 'green'
    case 'IN_PROGRESS':
    case 'PENDING':
    case 'RUNNING':
      return 'blue'
    case 'DEGRADED':
      return 'yellow'
    case 'DISABLED':
    case 'NOT_CONFIGURED':
    case 'CANCELED':
    case 'IDLE':
      return 'gray'
    case 'ERROR':
    case 'FAILED':
      return 'red'
    default:
      return accessible === true ? 'green' : 'gray'
  }
}

const formatResponseTime = (ms?: number | null) =>
  typeof ms === 'number' ? `${ms}ms` : null

export const defaultDeploymentsListVariables = {
  first: 100,
  skip: 0,
  filter: undefined,
  orderBy: [{ startedAt: 'DESC' as const }],
}
export const ControlsCard = ({
  disabled,
  reloading,
  clearingCache,
  onReload,
  onClearCache,
}: {
  disabled: boolean
  reloading: boolean
  clearingCache: boolean
  onReload: () => void
  onClearCache: () => void
}) => (
  <Card p="12px" minW={['100%', '100%', '360px']}>
    <Flex direction="column" gap="10px">
      <Text fontSize="sm" fontWeight="700">
        Controls
      </Text>
      <Flex gap="8px" wrap="wrap">
        <Button
          size="sm"
          variant="tertiary"
          onClick={onReload}
          isLoading={reloading}
          isDisabled={disabled || clearingCache}
        >
          Reload DB Subgraphs
        </Button>
        <Button
          size="sm"
          variant="tertiary"
          onClick={onClearCache}
          isLoading={clearingCache}
          isDisabled={disabled || reloading}
        >
          Clear Cache
        </Button>
      </Flex>
      {disabled && (
        <Text fontSize="xs" color="presets.neutral.70">
          Controls are available when the DB subgraphs manager is reachable.
        </Text>
      )}
    </Flex>
  </Card>
)

export const ServiceCard = ({
  service,
}: {
  service: CustomerDataApiServiceStatus
}) => (
  <Card p="14px">
    <Flex direction="column" gap="8px" minH="100%">
      <Flex justify="space-between" align="start" gap="8px">
        <Text fontSize="sm" fontWeight="700">
          {service.name}
        </Text>
        <AdminBadge
          colorScheme={statusColor(service.status, service.accessible)}
        >
          {service.accessible ? (service.status ?? 'READY') : 'NOT RUNNING'}
        </AdminBadge>
      </Flex>
      <Text fontSize="xs" color="presets.neutral.70">
        {service.message ??
          (service.accessible
            ? 'Service is reachable.'
            : 'Service unavailable.')}
      </Text>
      {service.error && (
        <Text fontSize="xs" color="red.500" noOfLines={3}>
          {service.error}
        </Text>
      )}
    </Flex>
  </Card>
)

export const LiveRuntimeCard = ({
  liveConfig,
  liveDeployment,
  artifacts,
}: {
  liveConfig?: { id: string; createdDate: string; updatedDate: string } | null
  liveDeployment?: CustomerDataApiStatusDeploymentRef | null
  artifacts: {
    id: string
    subgraphName: string
    smrtVersion: string
    smrtIri?: string
    resolvedFrom?: string | null
    introspectionHost?: string | null
  }[]
}) => {
  const resolvedFromLabel = (resolvedFrom?: string | null) => {
    switch (resolvedFrom) {
      case 'DB':
        return 'live (DB)'
      case 'PIN':
        return 'fallback (pin)'
      case 'SIDECAR':
        return 'fallback (sidecar)'
      default:
        return resolvedFrom ?? 'unknown'
    }
  }

  const resolvedFromColor = (resolvedFrom?: string | null) => {
    switch (resolvedFrom) {
      case 'DB':
        return 'green'
      case 'PIN':
      case 'SIDECAR':
        return 'yellow'
      default:
        return 'gray'
    }
  }

  return (
    <Card p="16px">
      <Flex direction="column" gap="14px">
        <Text fontSize="md" fontWeight="700">
          Current Live Runtime
        </Text>
        <Flex direction="column" gap="8px">
          <RuntimeLinkRow
            label="Config"
            value={
              liveConfig
                ? `Created ${formatAdminDateTime(liveConfig.createdDate)}`
                : 'No live config yet'
            }
            to={
              liveConfig
                ? `/admin/customer-data-api-config?configId=${liveConfig.id}`
                : '/admin/customer-data-api-config'
            }
          />
          <RuntimeLinkRow
            label="Deployment"
            value={
              liveDeployment
                ? `Attempt ${liveDeployment.attempt} · ${liveDeployment.status}`
                : 'No successful deployment yet'
            }
            to={
              liveDeployment
                ? `/admin/deployments/${liveDeployment.id}`
                : '/admin/deployments'
            }
          />
        </Flex>
        <Divider />
        <Flex direction="column" gap="8px">
          <Text fontSize="sm" fontWeight="600">
            Resolved SMRT Artifacts
          </Text>
          {artifacts.length > 0 ? (
            <Flex direction="column" gap="8px">
              {artifacts.map(artifact => (
                <Box
                  key={`${artifact.id}-${artifact.subgraphName}`}
                  borderWidth="1px"
                  borderRadius="8px"
                  borderColor="presets.neutral.20"
                  p="8px"
                >
                  <Flex justify="space-between" align="start" gap="8px">
                    <Text fontSize="sm" fontWeight="600">
                      {artifact.subgraphName}
                    </Text>
                    <AdminBadge
                      colorScheme={resolvedFromColor(artifact.resolvedFrom)}
                    >
                      {resolvedFromLabel(artifact.resolvedFrom)}
                    </AdminBadge>
                  </Flex>
                  <Text fontSize="xs" color="presets.neutral.70" mt="4px">
                    Version {artifact.smrtVersion}
                    {artifact.smrtIri ? ` · ${artifact.smrtIri}` : ''}
                  </Text>
                  {artifact.introspectionHost && (
                    <Text fontSize="xs" color="presets.neutral.70" mt="2px">
                      Introspection DB: {artifact.introspectionHost}
                    </Text>
                  )}
                </Box>
              ))}
            </Flex>
          ) : (
            <Text fontSize="xs" color="presets.neutral.70">
              No runtime SMRT artifacts are available from the manager yet.
            </Text>
          )}
        </Flex>
      </Flex>
    </Card>
  )
}

export const DeploymentCard = ({
  liveDeployment,
  inProgressDeployments,
  pipeline,
}: {
  liveDeployment?: CustomerDataApiStatusDeploymentRef | null
  inProgressDeployments: CustomerDataApiStatusDeploymentRef[]
  pipeline?: { running: boolean; deploymentId?: string | null }
}) => (
  <Card p="16px">
    <Flex direction="column" gap="12px">
      <Text fontSize="md" fontWeight="700">
        Deployment Activity
      </Text>
      <Flex direction="column" gap="8px">
        <Text fontSize="sm">
          Manager pipeline:{' '}
          <AdminBadge colorScheme={pipeline?.running ? 'blue' : 'gray'}>
            {pipeline?.running ? 'RUNNING' : 'IDLE'}
          </AdminBadge>
        </Text>
        {pipeline?.deploymentId && (
          <Button
            as={Link}
            to={`/admin/deployments/${pipeline.deploymentId}`}
            size="xs"
            variant="ghost"
            alignSelf="start"
          >
            Open running deployment
          </Button>
        )}
        <Text fontSize="xs" color="presets.neutral.70">
          Live deployment:{' '}
          {liveDeployment
            ? `attempt ${liveDeployment.attempt}, completed ${
                liveDeployment.completedAt
                  ? formatAdminDateTime(liveDeployment.completedAt)
                  : 'unknown'
              }`
            : 'none yet'}
        </Text>
      </Flex>
      <Divider />
      <Flex direction="column" gap="6px">
        <Text fontSize="sm" fontWeight="600">
          In-progress deployments
        </Text>
        {inProgressDeployments.length > 0 ? (
          inProgressDeployments.map(deployment => (
            <RuntimeLinkRow
              key={deployment.id}
              label={`Attempt ${deployment.attempt}`}
              value={`${deployment.status} · ${formatAdminDateTime(
                deployment.startedAt,
              )}`}
              to={`/admin/deployments/${deployment.id}`}
            />
          ))
        ) : (
          <Text fontSize="xs" color="presets.neutral.70">
            No deployment is currently running.
          </Text>
        )}
      </Flex>
    </Flex>
  </Card>
)

export const SubgraphRuntimeCard = ({
  subgraph,
}: {
  subgraph: CustomerDataApiRuntimeSubgraph
}) => (
  <Card p="16px">
    <Flex direction="column" gap="14px">
      <Flex justify="space-between" gap="12px" wrap="wrap">
        <Flex direction="column" gap="2px">
          <Text fontSize="md" fontWeight="700">
            {subgraph.subgraphName}
          </Text>
          <Text fontSize="xs" color="presets.neutral.70">
            {subgraph.path} · {subgraph.customers.length} customer
            {subgraph.customers.length === 1 ? '' : 's'}
          </Text>
        </Flex>
        <AdminBadge
          colorScheme={statusColor(subgraph.status, !subgraph.isDisabled)}
        >
          {subgraph.isDisabled ? 'DISABLED' : subgraph.status}
        </AdminBadge>
      </Flex>

      <SimpleGrid columns={[1, 1, 2]} spacing="12px">
        {subgraph.customers.map(customer => (
          <CustomerRuntimeCard
            key={`${subgraph.id}-${customer.organizationName}`}
            customer={customer}
          />
        ))}
      </SimpleGrid>

      {subgraph.recentLogs.length > 0 && (
        <Box bg="presets.neutral.10" borderRadius="8px" p="10px">
          <Text fontSize="xs" fontWeight="700" mb="4px">
            Recent logs
          </Text>
          <Flex direction="column" gap="2px">
            {subgraph.recentLogs.slice(-4).map((log, idx) => (
              <Text key={`${subgraph.id}-log-${idx}`} fontSize="xs">
                {log}
              </Text>
            ))}
          </Flex>
        </Box>
      )}
    </Flex>
  </Card>
)

const CustomerRuntimeCard = ({
  customer,
}: {
  customer: CustomerDataApiRuntimeCustomer
}) => (
  <Box
    border="1px solid"
    borderColor="presets.neutral.20"
    borderRadius="8px"
    p="12px"
  >
    <Flex direction="column" gap="10px">
      <Flex justify="space-between" gap="8px" align="start">
        <Flex direction="column" gap="2px">
          <Text fontSize="sm" fontWeight="700">
            {customer.organizationName}
          </Text>
          <Text fontSize="xs" color="presets.neutral.70">
            Version {customer.currentVersion ?? 'unknown'} ·{' '}
            {customer.databaseName ?? 'database unknown'}
          </Text>
        </Flex>
        <AdminBadge
          colorScheme={statusColor(customer.status, !customer.isDisabled)}
        >
          {customer.status}
        </AdminBadge>
      </Flex>

      <HealthRow
        label="Prisma DB"
        check={customer.database}
        detail={
          customer.host
            ? `${customer.host}:${customer.port ?? ''}`
            : 'No active host'
        }
      />
      <HealthRow
        label="Cube"
        check={
          customer.cube.configured ? customer.cube.reachability : undefined
        }
        detail={customer.cube.apiUrl ?? 'Not configured'}
      />
      {customer.cube.configured && (
        <HealthRow
          label="Cube introspection"
          check={customer.cube.introspection}
        />
      )}
    </Flex>
  </Box>
)

const HealthRow = ({
  label,
  check,
  detail,
}: {
  label: string
  check?: {
    accessible: boolean
    message?: string | null
    error?: string | null
    responseTimeMs?: number | null
  }
  detail?: string
}) => (
  <Flex direction="column" gap="4px">
    <Flex justify="space-between" align="center" gap="8px">
      <Text fontSize="xs" fontWeight="700">
        {label}
      </Text>
      <AdminBadge
        colorScheme={check ? (check.accessible ? 'green' : 'red') : 'gray'}
      >
        {check
          ? check.accessible
            ? 'ACCESSIBLE'
            : 'INACCESSIBLE'
          : 'NOT CONFIGURED'}
      </AdminBadge>
    </Flex>
    {detail && (
      <Text fontSize="xs" color="presets.neutral.70" noOfLines={1}>
        {detail}
      </Text>
    )}
    <Text
      fontSize="xs"
      color={check?.accessible ? 'presets.neutral.70' : 'red.500'}
      noOfLines={3}
    >
      {check?.message ??
        check?.error ??
        'No health check information is available.'}
      {formatResponseTime(check?.responseTimeMs)
        ? ` · ${formatResponseTime(check?.responseTimeMs)}`
        : ''}
    </Text>
  </Flex>
)

const RuntimeLinkRow = ({
  label,
  value,
  to,
}: {
  label: string
  value: string
  to: string
}) => (
  <Flex justify="space-between" gap="12px" align="center">
    <Text fontSize="xs" color="presets.neutral.70">
      {label}
    </Text>
    <Button as={Link} to={to} size="xs" variant="ghost" whiteSpace="normal">
      {value}
    </Button>
  </Flex>
)

export const StatusCallout = ({
  title,
  message,
  tone,
  action,
}: {
  title: string
  message: string
  tone: 'blue' | 'red' | 'yellow'
  action?: ReactNode
}) => (
  <Card
    p="14px"
    border="1px solid"
    borderColor={`${tone}.200`}
    bg={`${tone}.50`}
  >
    <Flex direction="column" gap="8px">
      <Text fontSize="sm" fontWeight="700">
        {title}
      </Text>
      <Text fontSize="sm">{message}</Text>
      {action}
    </Flex>
  </Card>
)
